import { memo, useContext, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector, useDebounce } from '@Hooks/index';
import {
  MAutoComplete,
  MButton,
  MInput,
  MInputNumber,
  MSelect,
  MTextArea,
} from '@Components/Base';

import { Col, Form, Row, Checkbox } from 'antd';

import { FormikProps } from 'formik';
import {
  currentMaterialHeaderRequest,
  currentMaterialItemInputs,
  currentMaterialRequest,
} from '@Features/MaterialRequest/requests/MaterialRequisition/store/selectors/materialRequestSelector';

import { CostCenterType } from '@Features/MaterialRequest/requests/MaterialRequisition/form/constants';
import { NotificationContext } from '@Context/NotificationContext';
import { materialRequestActions } from '@Features/MaterialRequest/requests/MaterialRequisition/store/materialRequestSlice';

import useFindResources from '../../../hook/useFindResources';
import { ResourceType } from '../../../types/ResourceType';

import { transformResponseToOptions } from '../utils/transformResponseToOptions';

import {
  MaterialItemRequestFormValues,
  SearchOption,
} from '../../../types/types';

import styles from './modal-content.module.css';

interface ModalContentProps {
  onDisableSaveButton: (value: boolean) => void;
  formik: FormikProps<MaterialItemRequestFormValues>;
  handleSubmit: (e?: React.FormEvent<HTMLFormElement> | undefined) => void;
  handleSaveMaterialItem: () => void;
  isEditMode?: boolean;
  isDisableSaveButton: boolean;
}

const ModalContent: React.FC<ModalContentProps> = ({
  formik,
  handleSubmit,
  handleSaveMaterialItem,
  isDisableSaveButton,
  isEditMode = false,
  onDisableSaveButton,
}) => {
  const { errorNotification } = useContext(NotificationContext);
  const dispatch = useAppDispatch();
  const { costCenterId, costCenterType, compId, unit } = useAppSelector(
    currentMaterialHeaderRequest
  );
  const { requestId } = useAppSelector(currentMaterialRequest);
  const { activity, ledger, material, plant, resource } = useAppSelector(
    currentMaterialItemInputs
  );
  const { findResource } = useFindResources();

  const [searchOptions, setOptions] = useState<Array<SearchOption>>([]);
  const [searchValue, setSearchValue] = useState('');
  const deferredQuery = useDebounce(searchValue.toLowerCase(), 300);
  const [requestType, setRequestType] = useState<ResourceType>();

  const resourceOptions = useMemo(() => {
    if (!searchOptions) return [];

    const transformedOptions = searchOptions.map((option) => {
      if (option.id) {
        return {
          label: `${option.code}~${option.id}~${option.desc}`,
          value: String(option.id),
        };
      }

      return {
        label: `${option.code}~${option.id}~${option.desc}`,
        value: option.code,
      };
    });

    return transformedOptions;
  }, [searchOptions]);

  const setInputValue = (label: string, type: string, trim?: boolean): void => {
    const trimmedLabel = trim ? label.trim() : label;
    if (type === 'actCode') {
      dispatch(materialRequestActions.setActivityInputValue(trimmedLabel));
    }
    if (type === 'resCode') {
      dispatch(materialRequestActions.setResourceInputValue(trimmedLabel));
    }
    if (type === 'matCode') {
      dispatch(materialRequestActions.setMaterialInputValue(trimmedLabel));
    }
    if (type === 'plantCode') {
      dispatch(materialRequestActions.setPlantInputValue(trimmedLabel));
    }
    if (type === 'ledgerCode') {
      dispatch(materialRequestActions.setLedgerInputValue(trimmedLabel));
    }
  };

  useEffect(() => {
    if (formik.values) {
      const { actCode, resCode, matCode, plantCode, ledgerCode } =
        formik.values;
      const tempObj = {
        actCode,
        resCode,
        matCode,
        plantCode,
        ledgerCode,
      } as { [key: string | number]: string | number };

      for (const key in tempObj) {
        if (key) {
          const value = String(tempObj[key]);
          setInputValue(value, key);
        }
      }
    }
  }, [formik.values]);

  useEffect(() => {
    let disableSaveButton = true;

    const formikValues = formik.values;

    if (CostCenterType.Project === costCenterType) {
      if (
        formikValues.actId &&
        formikValues.resCode &&
        formikValues.matId &&
        formikValues.reqQty
      ) {
        disableSaveButton = false;
      }
    }

    if (CostCenterType.Plant === costCenterType) {
      if (
        formikValues.plantId &&
        formikValues.ledgerId &&
        formikValues.matId &&
        formikValues.reqQty
      ) {
        disableSaveButton = false;
      }
    }

    if (
      CostCenterType.Plant === costCenterType ||
      CostCenterType.OverHead === costCenterType
    ) {
      if (formikValues.ledgerId && formikValues.matId && formikValues.reqQty) {
        disableSaveButton = false;
      }
    }

    onDisableSaveButton(disableSaveButton);
  }, [formik.values, isEditMode]);

  // eslint-disable-next-line
  const onSelect = (option: any, formikField: string): void => {
    const string = option.label.props.label || '';
    const [selectedCode, selectedId] = string.split('~');

    if (formikField !== 'resCode') {
      const codeField = formikField.replace('Id', 'Code');
      formik.setFieldValue(formikField, selectedId);

      formik.setFieldValue(codeField, selectedCode);
      setInputValue(selectedCode, codeField, true);
      return;
    }

    setInputValue(selectedCode, formikField, true);

    formik.setFieldValue(formikField, String(selectedCode));
  };

  const onClick = (type: ResourceType): void => {
    setRequestType(type);
    setSearchValue(' '); // to make request by on click the input and show all results
  };

  const handleCleanUp = (): void => {
    setSearchValue('');
    setOptions([]);
  };

  const handleSearch = (searchString: string, type: string): void => {
    setSearchValue(searchString);
    setInputValue(searchString, type);
  };

  useEffect(() => {
    if (deferredQuery && requestType) {
      findResource(
        {
          requisitionId: requestId,
          ccId: costCenterId!,
          searchValue: String(deferredQuery),
          compId: compId!,
        },
        requestType
      )
        ?.then((response) => {
          if (response.content) {
            const transformedResponse = transformResponseToOptions(
              response.content,
              requestType
            );

            setOptions(transformedResponse);
          }
        })
        .catch((message) => errorNotification(message));
    }
  }, [deferredQuery, requestType, costCenterId, requestId, compId]);

  return (
    <Form layout="vertical" onFinish={handleSubmit}>
      <Row className={styles.container}>
        {CostCenterType.Project === costCenterType && (
          <>
            <Col span={24} className={styles.col}>
              <Form.Item label="Activity Code">
                <MAutoComplete
                  dropdownItem="MR"
                  style={{ width: '100%' }}
                  options={resourceOptions}
                  onSelect={(_, option) => onSelect(option, 'actId')}
                  onSearch={(value) => handleSearch(value, 'actCode')}
                  onClick={() => onClick('Activities')}
                  onBlur={handleCleanUp}
                  value={activity}
                >
                  <MInput searchInput placeholder="Insert Activity Code" />
                </MAutoComplete>
              </Form.Item>
            </Col>

            <Col span={24} className={styles.colCheckbox}>
              <Form.Item>
                <Checkbox
                  className={styles.checkbox}
                  onChange={(event) => {
                    formik.setFieldValue(
                      'repeatForAll',
                      Number(event.target.checked)
                    );
                  }}
                >
                  <div className={styles.checkboxContent}>
                    <span className={styles.caption}>Repeat for all items</span>
                    <span className={styles.description}>
                      Check this box to use the same activity code for all
                      subsequent materials
                    </span>
                  </div>
                </Checkbox>
              </Form.Item>
            </Col>

            <Col span={24} className={styles.col}>
              <Form.Item label="Resource Code">
                <MAutoComplete
                  dropdownItem="MR"
                  style={{ width: '100%' }}
                  options={resourceOptions}
                  onSelect={(_, option) => onSelect(option, 'resCode')}
                  onSearch={(value) => handleSearch(value, 'resCode')}
                  onClick={() => onClick('Resources')}
                  onBlur={handleCleanUp}
                  value={resource}
                >
                  <MInput searchInput placeholder="Insert Resource Code" />
                </MAutoComplete>
              </Form.Item>
            </Col>

            <Col span={24} className={styles.colCheckbox}>
              <Form.Item>
                <Checkbox
                  className={styles.checkbox}
                  onChange={(event) => {
                    formik.setFieldValue(
                      'repeatForAll',
                      Number(event.target.checked)
                    );
                  }}
                >
                  <div className={styles.checkboxContent}>
                    <span className={styles.caption}>Repeat for all items</span>
                    <span className={styles.description}>
                      Check this box to use the same resource code for all
                      subsequent materials
                    </span>
                  </div>
                </Checkbox>
              </Form.Item>
            </Col>
          </>
        )}

        {CostCenterType.Plant === costCenterType && (
          <Col span={24} className={styles.col}>
            <Form.Item label="Plant No.">
              <MAutoComplete
                dropdownItem="MR"
                style={{ width: '100%' }}
                options={resourceOptions}
                onSelect={(_, option) => onSelect(option, 'plantId')}
                onSearch={(value) => handleSearch(value, 'plantCode')}
                onClick={() => onClick('Plants')}
                onBlur={handleCleanUp}
                value={plant}
              >
                <MInput searchInput placeholder="Type Plant No." />
              </MAutoComplete>
            </Form.Item>
          </Col>
        )}

        {(CostCenterType.Plant === costCenterType ||
          CostCenterType.OverHead === costCenterType) && (
          <Col span={24} className={styles.col}>
            <Form.Item label="Ledger Code">
              <MAutoComplete
                dropdownItem="MR"
                style={{ width: '100%' }}
                options={resourceOptions}
                onSelect={(_, option) => onSelect(option, 'ledgerId')}
                onSearch={(value) => handleSearch(value, 'ledgerCode')}
                onClick={() => onClick('Ledgers')}
                onBlur={handleCleanUp}
                value={ledger}
              >
                <MInput searchInput placeholder="Insert Ledger Code" />
              </MAutoComplete>
            </Form.Item>
          </Col>
        )}

        <Col span={24} className={styles.col}>
          <Form.Item label="Material Code">
            <MAutoComplete
              dropdownItem="MR"
              style={{ width: '100%' }}
              options={resourceOptions}
              onSelect={(_, option) => onSelect(option, 'matId')}
              onSearch={(value) => handleSearch(value, 'matCode')}
              onClick={() => onClick('Materials')}
              onBlur={handleCleanUp}
              value={material}
            >
              <MInput searchInput placeholder="Search Material Code" />
            </MAutoComplete>
          </Form.Item>
        </Col>

        <Col span={24} className={styles.col}>
          <Form.Item label="Unit">
            <MSelect
              labeloption="Unit"
              value={unit}
              placeholder="Select Unit"
              disabled
            />
          </Form.Item>
        </Col>

        <Col span={24} className={styles.col}>
          <Form.Item label="Qty.">
            <MInputNumber
              name="reqQty"
              valueNum={formik.values.reqQty}
              onChange={(value: number) => {
                formik.setFieldValue('reqQty', value);
              }}
            />
          </Form.Item>
        </Col>

        <Col span={24} className={styles.col}>
          <Form.Item label="Remarks">
            <MTextArea
              name="transferRemarks"
              value={formik.values.transferRemarks}
              onChange={formik.handleChange}
              rows={4}
              showCount
              maxLength={100}
              placeholder="Insert Remarks"
            />
          </Form.Item>
        </Col>

        {!isEditMode && (
          <Col span={24} className={styles.col}>
            <MButton
              onClick={handleSaveMaterialItem}
              className={styles.buttonSave}
              type="secondary"
              disabled={isDisableSaveButton}
            >
              Save & Add Another
            </MButton>
          </Col>
        )}
      </Row>
    </Form>
  );
};

export default memo(ModalContent);
